import { AlertIconDark, AlertIconLight } from "@images/svgIcons/alertIcon"
import { useStripeAccount } from "@modules/finances/hooks/useStripeAccount"
import { useStripeAccountErrors } from "@modules/finances/hooks/useStripeAccountErrors"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { ThemeMode } from "@modules/ui/styles/theme"
import { Carousel } from "primereact/carousel"
import { useTheme } from "styled-components"

export const StripeAccountErrorAlert: React.FC<{ orientation: "vertical" | "horizontal" }> = ({ orientation }) => {
  const formattedStripeAccountErrors = useStripeAccountErrors()
  const { createStripeAccount, updateStripeAccount, useStripeAccountStatus, loading } = useStripeAccount()
  const { stripeAccountStatus } = useStripeAccountStatus()

  const openStripeAccount = () => {
    if (stripeAccountStatus === "OK" || stripeAccountStatus === "REQUIRE_ADDITIONAL_INFORMATION") updateStripeAccount()
    else createStripeAccount()
  }

  if (!formattedStripeAccountErrors || formattedStripeAccountErrors.length === 0) return

  return (
    <div className={orientation === "vertical" ? "mb-4" : ""} data-testid="stripe-account-errors">
      <Carousel
        value={formattedStripeAccountErrors}
        itemTemplate={(item) => (
          <StripeAccountErrorItem
            {...item}
            orientation={orientation}
            primaryButtonOnClickFn={() => {
              item.openStripeAccountOnButtonClick ? openStripeAccount() : item.primaryButtonOnClickFn?.()
            }}
            primaryButtonLoading={loading}
          />
        )}
        showIndicators={formattedStripeAccountErrors.length > 1}
        showNavigators={false}
      />
    </div>
  )
}

export type StripeAccountErrorItemType = {
  alertTitle: string
  alertSubtitle?: string
  alertContent: string
  primaryButtonLabel?: string
  primaryButtonOnClickFn?: () => void
  primaryButtonLoading?: boolean
  openStripeAccountOnButtonClick?: boolean
}

export const StripeAccountErrorItem: React.FC<
  StripeAccountErrorItemType & { orientation: "vertical" | "horizontal"; size?: "M" | "S" }
> = ({
  alertTitle,
  alertSubtitle,
  alertContent,
  primaryButtonLabel,
  primaryButtonOnClickFn,
  primaryButtonLoading = false,
  orientation,
  size = "M",
}) => {
  const theme = useTheme()

  return (
    <div className="flex flex-row h-full rounded-big-radius overflow-hidden">
      <div className="bg-red-500 h-auto" style={{ minWidth: 10 }} />

      <div
        className={
          "bg-neutralColor-100 border-noround-left flex-grow-1 flex " +
          (orientation === "vertical"
            ? "flex-column justify-content-center align-items-center"
            : "flex-row align-items-center justify-content-between") +
          (size === "M" ? " px-4 py-3 gap-4" : " px-3 py-3 gap-3")
        }
      >
        <div className="flex flex-row align-items-center justify-content-center gap-2">
          {theme.mode === ThemeMode.ATHLETE ? (
            <AlertIconLight color={theme.error} />
          ) : (
            <AlertIconDark color={theme.error} />
          )}
          <HUText fontStyle={size === "M" ? "TM" : "TS"} className="text-center">
            {alertTitle}
          </HUText>
        </div>

        {alertSubtitle && (
          <HUText fontStyle="TS" className={orientation === "vertical" ? "align-self-start" : ""}>
            {alertSubtitle}
          </HUText>
        )}

        <HUText
          fontStyle={size === "M" ? "BM" : "BS"}
          color={theme.neutralColor600}
          className={orientation === "vertical" ? "align-self-start" : ""}
          style={{ whiteSpace: "pre-line" }}
        >
          {alertContent}
        </HUText>

        {primaryButtonLabel && primaryButtonOnClickFn && (
          <HUButton
            colorType="Primary"
            size={size}
            type="Default"
            text={primaryButtonLabel}
            onClick={primaryButtonOnClickFn}
            loading={primaryButtonLoading}
            disabled={primaryButtonLoading}
          />
        )}
      </div>
    </div>
  )
}
